<template>
  <div :class="['tree-select', { 'tree-select--multiselect': multiselect}]" ref="container">
    <el-popover
      :popper-options="{boundariesElement:'viewport'}"
      :popper-class="popperClass"
      placement="bottom-start"
      :width="popoverWidth"
      trigger="click"
      v-model="showPopover"
      :visible-arrow="false"
      ref="popover"
      :disabled="disabled"
      @show="eventEmitter('showPopover')">
      <div slot="reference" class="tree-select__input" :class="{'is-disabled': disabled}">
        <div class="tree-select__placeholder" v-if="checkedNodes.length == 0">
          {{ placeholder }}
        </div>
        <div v-else class="tree-select__tags">
          <div v-for="node in checkedNodes" :key="node[nodeKey]" class="tree-select__checked-org">
            {{ getNodeLabel(node) }}
          </div>
        </div>
        <i class="tree-select__caret el-icon-arrow-up"></i>
      </div>
      <div class="tree-select__popover" ref="popoverContent">
        <el-input
          :placeholder="$t('labels.search')"
          prefix-icon="el-icon-search"
          class="tree-select__search"
          v-model="filterText">
        </el-input>
        <div class="tree-select__popover-container">
          <ui-scrollbar>
            <tree
              :data="actualList"
              :props="treeProps"
              @check="nodeCheck"
              :default-checked-keys="defaultCheckedKeys"
              :default-expanded-keys="defaultExpandedKeys"
              show-checkbox
              :check-strictly="!multiselect"
              :node-key="nodeKey"
              :lazy="lazy"
              :load="load ? load : undefined"
              :class="['tree-select__tree', { 'tree-select__tree--multiselect': multiselect}]"
              :filter-node-method="filterNode"
              :indent="treeIndent"
              :showCheckboxFor="showCheckboxFor"
              :check-on-click-node="checkOnClickNode"
              :expand-on-click-node="expandOnClickNode"
              ref="tree">
                    <span class="el-tree-node__label" slot-scope="{ node }">
                      {{ getNodeLabel(node.data) }}
                    </span>
            </tree>
          </ui-scrollbar>
        </div>
        <div class="tree-select__button" v-if="showButton">
          <span @click="buttonClick">{{ buttonText || $t('labels.button') }}</span>
        </div>
      </div>
    </el-popover>
  </div>
</template>
<script>
  import lodash from 'lodash'
  import tree from '@/components/Tree/Tree'

  const elementResizeDetectorMaker = require('element-resize-detector');

  export default {
    props: {
      data: {
        type: Array,
        default: () => {
          return []
        }
      },
      props: {
        type: Object,
        default: function () {
          return {}
        }
      },
      checkNode: {
        type: Boolean,
        default: true
      },
      itemIdent: {
        type: String,
        default: '_item'
      },
      nodeKey: {
        type: String,
        default: 'id'
      },
      placeholder: {
        type: String,
        default: ''
      },
      defaultCheckedKeys: {
        type: Array,
        default: function () {
          return []
        }
      },
      defaultCheckedNodes: {
        type: Array,
        default: function () {
          return []
        }
      },
      defaultExpandedKeys: {
        type: Array,
        default: function () {
          return []
        }
      },
      index: {
        type: Number
      },
      multiselect: {
        type: Boolean,
        default: false
      },
      showButton: Boolean,
      buttonText: String,
      popperClass: {
        default: ''
      },
      treeIndent: {
        type: Number,
        default: 16
      },
      popoverWidth: {
        type: String,
        default: '400'
      },
      disabled: {
        type: Boolean,
        default: false
      },
      lazy: Boolean,
      load: Function,
      showCheckboxFor: String,
      checkOnClickNode: Boolean,
      expandOnClickNode: {
        type: Boolean,
        default: true
      },
      searchType: {
        type: String,
        default: 'user'
      },
      searchLimit: {
        type: Number,
        default: 20
      }
    },
    components: {
      tree
    },
    data () {
      return {
        searchTimer: 0,
        searchList: false,
        updatePending: false,
        checkedNodes: [],
        filterText: '',
        treeProps: {
          label: this.props.label || 'name',
          children: this.props.children || 'children',
          disabled: this.props.disabled || 'disable',
          isLeaf: this.props.isLeaf || 'leaf'
        },
        showPopover: false,
        resizeUpdateDiffer: null
      }
    },
    methods: {
      bindResize () {
        this.resizeDetector = elementResizeDetectorMaker({
          strategy: 'object',
          callOnAdd: true
        });
        this.resizeDetector.listenTo(this.$refs.popoverContent, () => {
          clearTimeout(this.resizeUpdateDiffer)
          this.resizeUpdateDiffer = setTimeout(() => {
            requestAnimationFrame(() => {
              if (this.$refs.popover.popperJS) {
                this.$refs.popover.popperJS.update()
              }
              this.updatePending = false
            })
          }, 50)
        })
      },
      offResize () {
        if (this.resizeDetector) {
          this.resizeDetector.uninstall(this.$refs.popoverContent)
          this.resizeDetector = null
        }
      },
      getNodeLabel (data) {
        let path = this.treeProps.label
        return lodash.get(data, path, '????.????.????')
      },
      filterNode (value, data) {
        if (!value) return true;
        return data.name.toLowerCase().indexOf(value.toLowerCase()) !== -1;
      },
      nodeCheck (data, checked) {
        if (!this.multiselect) {
          this.checkedNodes = []
          this.checkedNodes.push(data)
          this.showPopover = false;
          this.$emit('checked', data, checked, this.index)
          if (checked) {
            this.$refs.tree.setCheckedKeys([data.id]);
          }
        } else {
          this.addCheckedNodes(checked.checkedNodes);
          this.$emit('checked', data, checked, this.index)
        }
      },
      addCheckedNodes (data) {
        this.checkedNodes = []
        if (data.length > 0) {
          for (let item of data) {
            if (!this.checkNode) {
              if (item[this.itemIdent]) this.checkedNodes.push(item)
            } else {
              this.checkedNodes.push(item)
            }
          }
        }
      },
      setCheckedKeys () {
        this.checkedNodes = this.$refs.tree.getCheckedNodes()
      },
      setCheckedNodes (nodes) {
        this.checkedNodes = nodes
      },
      setDefaultCheckedKeys () {
        const nodes = []
        for (const key of this.defaultCheckedKeys) {
          const node = this.findNodeInTree(this.data, key)
          if (node) {
            nodes.push(node)
          }
        }
        if (nodes.length) {
          this.checkedNodes = nodes
        }
      },
      findNodeInTree (items, needId) {
        if (items) {
          for (let node of items) {
            if (node[this.nodeKey] === needId) {
              return node
            }
            let inChilds = this.findNodeInTree(node[this.props.children], needId)
            if (inChilds) {
              return inChilds
            }
          }
        }
        return false
      },
      buttonClick () {
        this.$emit('buttonClick');
        this.showPopover = false;
      },
      reset () {
        this.checkedNodes = []
        this.$refs.tree.setCheckedKeys([]);
        this.filterText = ''
      },
      eventEmitter (event) {
        this.$emit(event)
      },
      async makeSearch (search) {
        clearTimeout(this.searchTimer)
        if (search) {
          this.searchTimer = setTimeout(async () => {
            const result = await this.$store.dispatch('search/find', {search, type: this.searchType, limit: this.searchLimit})
            const list = [];
            for (const item of result.result.documents) {
              list.push({
                name: item.snippet.name,
                _id: item.id
              })
            }
            this.searchList = list
          }, 500)
        } else {
          this.searchList = false
        }
      }
    },
    async mounted () {
      this.$nextTick(() => {
        if (this.defaultCheckedKeys.length) {
          this.$refs.tree.setCheckedKeys(this.defaultCheckedKeys)
          this.setDefaultCheckedKeys()
        }
      })
      this.bindResize()
    },
    beforeDestroy () {
      this.offResize()
    },
    watch: {
      data () {
        this.$nextTick(() => {
          if (this.defaultCheckedKeys.length) {
            // this.$refs.tree.setCheckedKeys(this.defaultCheckedKeys)
            // this.setDefaultCheckedKeys()
          }
        })
      },
      defaultCheckedKeys () {
        if (this.defaultCheckedKeys.length) {
          this.$refs.tree.setCheckedKeys(this.defaultCheckedKeys)
          this.setDefaultCheckedKeys()
        }
      },
      filterText (val) {
        if (this.data && this.data.length) {
          this.$refs.tree.filter(val);
        } else {
          this.makeSearch(val)
        }
      }
    },
    computed: {
      actualList () {
        return this.searchList !== false ? this.searchList : this.data
      }
    }
  }
</script>
<style lang="scss">
  .tree-select {

    &__input {
      position: relative;
      padding: 6px 40px 6px 0;
      max-width: 400px;
      min-height: 40px;
      max-height: 200px;
      overflow-x: auto;
      background-color: #fff;
      border-radius: 4px;
      border: 1px solid #dcdfe6;
      color: #c0c4cc;
      font-size: inherit;
      // line-height: 40px;
      outline: 0;
      -webkit-transition: border-color .2s cubic-bezier(.645, .045, .355, 1);
      transition: border-color .2s cubic-bezier(.645, .045, .355, 1);
      cursor: pointer;

      &:not(.is-disabled):hover {
        border-color: #c0c4cc;
      }

      &.is-disabled {
        background: rgba(192, 196, 204, 0.1);

        &:hover {
          cursor: not-allowed;
        }
      }
    }

    &__placeholder {
      line-height: 26px;
      padding-left: 15px;
    }

    &__tags {
      line-height: normal;
      white-space: normal;
      z-index: 1;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }

    &__checked-org {
      display: inline-block;
      margin: 2px 0 2px 6px;
      padding: 0 8px;
      line-height: 22px;
      border-radius: 4px;
      background-color: #f0f2f5;
      color: #909399;
    }

    &__caret {
      position: absolute;
      right: 15px;
      top: 50%;
      color: #c0c4cc;
      font-size: 14px;
      transform: rotateZ(180deg) translateY(50%);
      line-height: 16px;
      cursor: pointer;
    }

    &__popover-container {
      max-height: 250px;
      overflow: hidden;
    }

    &__popover {
      padding: 10px 0;
    }

    &__tree {
      .el-tree-node {
        &__content {
          height: auto;
          padding: 3px 0;
          min-height: 26px;
        }

        &__label {
          white-space: normal;
        }
      }

      .el-checkbox {
        &__inner {
          border-radius: 100%;

          &:after {
            width: 4px;
            height: 4px;
            border-radius: 100%;
            border: 0;
            background-color: #fff;
            content: "";
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%) scale(0);
            transition: transform .15s ease-in;
          }
        }

        &__input {
          &.is-checked {
            .el-checkbox__inner::after {
              transform: translate(-50%, -50%) scaleY(1);
            }
          }
        }
      }

      .el-tree-node__content > .el-checkbox {
        margin-top: 5px;
      }

      &--multiselect {
        .el-checkbox {
          &__inner {
            border-radius: 2px;

            &:after {
              border-radius: 0;
              border: 1px solid #fff;
              transform: rotate(45deg) scaleY(0);
              border-left: 0;
              border-top: 0;
              height: 7px;
              width: 3px;
              left: 4px;
              top: 1px;
              background: none;
            }
          }

          &__input {
            &.is-checked {
              .el-checkbox__inner::after {
                transform: rotate(45deg) scaleY(1);
              }
            }
          }
        }
      }
    }

    &__search {
      margin-bottom: 15px;
    }

    &__button {
      padding-top: 8px;
      text-align: center;

      span {
        color: #2196F3;
        cursor: pointer;

        &:hover {
          color: lighten(#2196F3, 15%);
        }
      }
    }
  }
</style>
